import * as React from 'react';
import { useSideNavigation } from '../../queries/side-navigation';
import SideNavSection from '../side-nav-section/side-nav-section';
import Anchor from '../anchor/anchor'

import * as styles from './side-nav.module.scss'

import { useCurrentNavSegments } from '../../hooks/current-nav-segments';
import { getFirstNavigableNode } from '../../hooks/current-nav-segments'
import FilterInput from '../filter-input/filter-input';
import Chevron from '../icons/chevron';

const createFilterContext = (root, text) => {
    const result = {
        text: text,
        visible: new Set(),
        expanded: new Set(),
        matches: new Set()
    };

    const queue = root.childrenSegments.map(n => ({
        ancestors: [],
        node: n
    }));

    while (queue.length > 0) {
        const { node, ancestors } = queue.pop();

        const isMatch = node.title.toLowerCase().indexOf(text.toLowerCase()) >= 0;

        if (isMatch) {
            result.visible.add(node);
            result.matches.add(node);

            ancestors.forEach(n => {
                result.visible.add(n);
                result.expanded.add(n)
            });
        } else {
            if (ancestors.some(a => result.matches.has(a))) {
                result.visible.add(node);
            }
        }

        if (node.childrenSegments) {
            const newAncestors = [node, ...ancestors];

            const nextItems = node.childrenSegments.map(n => ({
                ancestors: newAncestors,
                node: n
            }));

            queue.push(...nextItems);
        }
    }

    return result;
}

const scrollToTop = (element) => {
    if (!element) {
        return;
    }

    element.scrollTop = 0;
}

const SideNav = ({ topPx }) => {
    const navRoot = useSideNavigation();
    const [filterText, setFilterText] = React.useState(``);
    const [filterContext, setFilterContext] = React.useState(null);
    const sideNavScrollWrapRef = React.useRef(null);

    const currentNavSegments = useCurrentNavSegments();

    const backUrl = React.useMemo(() => getFirstNavigableNode(navRoot)?.relativeUrl, [navRoot])

    const currentRoot = React.useMemo(() => {
        const result = [...currentNavSegments].reverse().find(n => n.subRoot) || navRoot;
        return result;
    }, [navRoot, currentNavSegments])

    React.useEffect(() => {
        setFilterText(null);
        setFilterContext(null);
        scrollToTop(sideNavScrollWrapRef.current);
    }, [currentRoot]);

    React.useEffect(() => {
        scrollToTop(sideNavScrollWrapRef.current);
    },[filterContext]);

    React.useEffect(() => {
        let timeoutId = setTimeout(() => {
            timeoutId = null;

            const context = filterText ? createFilterContext(currentRoot, filterText) : null;
            setFilterContext(context);
        }, 500);

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    }, [filterText, currentRoot]);

    return (<>
        <div ref={sideNavScrollWrapRef} className={"e2e-left-nav " + styles.container + " dark-scrollbar"} style={{ top: `${topPx}px`, maxHeight: `calc(100vh - ${topPx}px)` }}>
            {currentRoot !== navRoot && <>
                <div className={styles.filterWrap}>
                    <Anchor className={styles.back} href={backUrl}>
                        <Chevron className={styles.arrow}></Chevron>
                        <span>Back to all docs</span>
                    </Anchor>
                    <FilterInput placeholder='Filter ...' onChange={val => setFilterText(val)}></FilterInput>
                </div>
            </>}
            {currentRoot.childrenSegments.filter(n => !n.hidden).map(n => <SideNavSection capitalize={!currentRoot.subRoot} filterContext={filterContext} key={n.id} navNode={n}></SideNavSection>)}
            {
                filterContext?.matches?.size <= 0 && <>
                    <div className={styles.noResults}>
                        <div className={styles.noResultsTitle}>No results found.</div>
                        <div className={styles.noResultsSubTitle}>
                            <div>Filter tips:</div>
                            <ul>
                                <li>Make sure all words are spelled correctly.</li>
                                <li>Try with the global search.</li>
                            </ul>
                        </div>
                    </div>
                </>
            }
        </div>
    </>)
}

export default SideNav;